/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';

import FloatToPercent from 'bundles/primitive-formatting/components/FloatToPercent';
import numberRenders from 'bundles/teach-course-shared/lib/numberRenders';
import type { RatingCountsByStarFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCourseEntitiesFragments';

import _t from 'i18n!nls/xdp';

type Props = {
  averageFiveStarRating?: number | null;
  ratingCountsByStar?: RatingCountsByStarFragment | null;
  ratingCount?: number | null;
};

const styles = {
  barGraphContainer: () => css`
    max-width: 315px;
    padding: 0 0 var(--cds-spacing-300) 0;
    vertical-align: middle;
    display: table;
    border-collapse: collapse;
    white-space: nowrap;

    li {
      border-bottom: var(--cds-spacing-100) solid transparent;
    }

    ${breakpoints.down('sm')} {
      max-width: 100%;
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  graphRow: css`
    width: 100%;
    display: table-row;
  `,
  starBarLabel: css`
    display: table-cell;
    position: relative;
    vertical-align: middle;
  `,
  barGraphBar: css`
    height: 6px;
    width: 100%;
    display: table-cell;
    vertical-align: middle;
    padding-top: 2px;
    position: relative;
    white-space: nowrap;
  `,
  numRatings: css`
    white-space: nowrap;
    display: table-cell;
    vertical-align: middle;
  `,
  // Ref: https://codepen.io/jedtrow/pen/YPrqKY
  greyBar: css`
    content: '';
    top: 0;
    left: 0;
    width: 100%;
    position: relative;
    height: 6px;
    background: var(--cds-color-grey-50);
    border-radius: 8px;
  `,
  blueBar: (maxWidthAsPercentage: string) => css`
    content: '';
    top: 0;
    left: 0;
    position: relative;
    height: 6px;
    background: var(--cds-color-blue-700);
    display: block;
    width: ${maxWidthAsPercentage};
    border-radius: 8px;
  `,
  ratingsSummary: css`
    display: inline-flex;
    align-items: baseline;
    margin-bottom: var(--cds-spacing-300);
  `,
  star: css`
    color: var(--cds-color-blue-700);
    margin-right: var(--cds-spacing-50);
  `,
  reviewCount: css`
    margin-left: var(--cds-spacing-150);
  `,
  rowWhiteSpace: css`
    display: inline-block;
    width: 0.5em;
  `,
};

const wordsToNumbers: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
};

const ReviewsRatingBarGraph = ({ averageFiveStarRating, ratingCountsByStar, ratingCount }: Props) => {
  if (!ratingCountsByStar || !ratingCount) {
    return null;
  }

  const totalStarRatingCounts = Object.values(ratingCountsByStar).reduce((acc: number, curr) => {
    if (curr && typeof curr === 'number') {
      return acc + curr;
    }
    return acc;
  }, 0);

  return (
    <React.Fragment>
      <span css={styles.ratingsSummary}>
        <StarFilledIcon color="interactive" css={styles.star} />
        <Typography variant="h1semibold" component="span">
          {averageFiveStarRating?.toFixed(1)}
        </Typography>
        <Typography2 component="p" variant="bodySecondary" css={styles.reviewCount}>
          <FormattedMessage
            message={_t('{ratingCount} reviews')}
            ratingCount={<FormattedNumber value={ratingCount} />}
          />
        </Typography2>
      </span>
      {totalStarRatingCounts > 0 && (
        <div data-unit="reviews-bar-graph" data-testid="reviews-bar-graph">
          <ul css={styles.barGraphContainer}>
            {Object.entries(ratingCountsByStar).map(([starValue, starCount]) => {
              const starCountNum = Number(starCount);
              const percentageAsDecimal = starCountNum / totalStarRatingCounts;
              const starPercentage = numberRenders.Formatters.percentage(percentageAsDecimal);
              const starLabel = starValue.includes('oneStar') ? 'star' : 'stars';
              const numStarIndex = wordsToNumbers[starValue.substring(0, starValue.indexOf('Star'))];

              if (!numStarIndex) {
                return null;
              }

              return (
                <li key={`${numStarIndex}-star`} css={styles.graphRow}>
                  <span css={styles.starBarLabel}>
                    <Typography2 variant="subtitleMedium" component="span">
                      {_t('#{numStarIndex} #{starLabel}', {
                        numStarIndex,
                        starLabel,
                      })}
                    </Typography2>
                  </span>
                  <span css={styles.rowWhiteSpace} />
                  <span css={styles.barGraphBar}>
                    <div css={styles.greyBar}>
                      <div css={styles.blueBar(starPercentage)} />
                    </div>
                  </span>
                  <span css={styles.rowWhiteSpace} />
                  <span css={styles.numRatings}>
                    <Typography2 component="p" variant="bodySecondary">
                      <FloatToPercent value={percentageAsDecimal} />
                    </Typography2>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </React.Fragment>
  );
};

export default ReviewsRatingBarGraph;
